/**
 * DashboardMainResponsive — Main dashboard layout component
 * SRP: MobileMenu → DashboardMobileMenu.tsx, Sidebar → DashboardSidebar.tsx
 */

import {
  BarChart2,
  Calculator,
  ChevronLeft,
  ChevronRight,
  FileText,
  LogOut,
  Menu,
  UserCheck,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompanyStore } from '../../../stores/companyStore';
import { authAPI, getAssetUrl } from '../../../utils/api';
import { Button } from '@/components/ui';
import { BottomNavigation, NavItem } from '@/components/ui-v2/BottomNavigation';
import { useNavigationBadges } from '../../../hooks/useNavigationBadges';
import { CompanySwitcher } from '@/components/ui-v2/CompanySwitcher';
import { ProfileWidget } from '@/components/ui-v2/ProfileWidget';
import { TrialBanner } from '@/features/subscriptions';
import { useTrialGuard } from '@/hooks/useTrialGuard';
import { useUserBackground } from '@/features/auth/hooks/useUserBackground';
import type { User } from '@/features/auth/types/auth.types';
import { useUIMode } from '@/hooks/useUIMode';
import { MobileMenu } from './DashboardMobileMenu';
import { buildOptimizedSidebar } from './DashboardSidebar';

export type Tab =
  | 'dashboard'
  | 'quotes'
  | 'invoices'
  | 'recurring-invoices'
  | 'credit-notes'
  | 'public-links'
  | 'products'
  | 'clients'
  | 'suppliers'
  | 'reminders'
  | 'payments/migration'
  | 'accounting'
  | 'chart-of-accounts'
  | 'journal-entries'
  | 'general-balance'
  | 'general-ledger'
  | 'accounting-exports'
  | 'imports'
  | 'imports/history'
  | 'account-reconciliation'
  | 'financial-statements'
  | 'fiscal-year-closure'
  | 'tax-rates'
  | 'tva'
  | 'fiscal-years'
  | 'reminder-policies'
  | 'treasury'
  | 'expenses/recurring'
  | 'treasury/budgets'
  | 'email-logs'
  | 'audit-logs'
  | 'webhooks'
  | 'system-overview'
  | 'user-provisioning'
  | 'certificates'
  | 'breach-check'
  | 'achats'
  | 'expenses'
  | 'purchases'
  | 'chorus-pro'
  | 'probatory-archive'
  | 'bank-reconciliation'
  | 'three-way-match'
  | 'ocr-extraction'
  | 'accounting-connectors'
  | 'settings'
  | 'settings/company'
  | 'settings/legal'
  | 'settings/payments'
  | 'settings/integrations'
  | 'settings/appearance'
  | 'settings/security'
  | 'users'
  | 'companies'
  | 'templates'
  | 'email-templates'
  | 'legal-mentions'
  | 'api-keys'
  | 'signatures'
  | 'benchmarks'
  | 'financing'
  | 'ai-actions'
  | 'support'
  | 'help'
  | 'interventions'
  | 'timesheets'
  | 'projects'
  | 'work-orders'
  | 'planning'
  | 'field-mode'
  | 'crm-vente'
  | 'leads'
  | 'opportunities'
  | 'sales-tasks'
  | 'automation-rules'
  | 'erp-cockpit'
  | 'erp-terrain'
  | 'erp-collaboration'
  | 'erp-portfolio'
  | 'erp-projects'
  | 'erp-situations'
  | 'erp-planning'
  | 'erp-teams'
  | 'erp-documents'
  | 'erp-materials'
  | 'erp-subcontracting'
  | 'erp-ma-journee'
  | 'erp-facturer'
  | 'erp/accueil'
  | 'erp/argent'
  | 'erp/terrain'
  | 'erp/dossiers'
  | 'accounting-entries'
  | 'accounting-balance'
  | 'accounting-declarations'
;

interface DashboardMainResponsiveProps {
  onLogout: () => void;
  user: User;
}

// Header responsive avec menu hamburger
const DashboardHeader: React.FC<{
  user: User;
  onLogout: () => void;
  onMenuClick: () => void;
}> = ({ user, onLogout, onMenuClick }) => {
  const { company } = useCompanyStore();
  const { t } = useTranslation();

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40 print:hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo et titre */}
          <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
            <img
              src={getAssetUrl(company?.logoUrl || '')}
              alt="Logo"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0"
              onError={(e) => {
                e.currentTarget.src = '/logo.svg';
              }}
            />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                {company?.legalName || t('nav.company.defaultName')}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                {t('nav.company.subtitle')}
              </p>
            </div>
          </div>

          {/* Actions desktop */}
          <div className="hidden md:flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user?.username}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout} icon={<LogOut className="w-4 h-4" />}>
              {t('nav.tooltips.logout')}
            </Button>
          </div>

          {/* Menu hamburger mobile */}
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            aria-label={t('nav.tooltips.menu')}
          >
            <Menu className="w-6 h-6 text-gray-700" />
          </button>
        </div>
      </div>
    </header>
  );
};

export const DashboardMainResponsive: React.FC<DashboardMainResponsiveProps> = ({
  onLogout,
  user,
}) => {
  const { t } = useTranslation();
  const { loadCompany } = useCompanyStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>('sales');
  const { badges } = useNavigationBadges();
  const location = useLocation();
  const navigate = useNavigate();

  // Trial Guard - Redirige vers /pricing si le trial est expiré
  useTrialGuard();

  // User background - Récupère le style de background personnalisé
  const { backgroundStyle, hasCustomBackground } = useUserBackground();

  // UI Mode - Mode Simple/Expert pour adapter l'interface
  const { shouldShowSection } = useUIMode();

  // Déterminer l'onglet actif basé sur l'URL
  const getActiveTab = (): Tab => {
    const path = location.pathname.split('/')[1] || 'dashboard';
    return path as Tab;
  };

  const activeTab = getActiveTab();

  // Charger les données de la société au montage du composant
  useEffect(() => {
    if (user?.companyId) {
      loadCompany(user.companyId);
    }
  }, [user?.companyId, loadCompany]);

  const handleLogout = () => {
    authAPI.logout();
    onLogout();
  };

  const handleTabChange = (tab: Tab) => {
    navigate(`/${tab}`);
  };

  // Navigation items pour bottom bar mobile
  const navItems: NavItem[] = [
    { id: 'dashboard', label: t('nav.bottomNav.dashboard'), icon: BarChart2 },
    { id: 'quotes', label: t('nav.bottomNav.sales'), icon: FileText },
    { id: 'clients', label: t('nav.bottomNav.clients'), icon: UserCheck },
    { id: 'accounting', label: t('nav.bottomNav.accounting'), icon: Calculator },
    { id: 'menu', label: t('nav.bottomNav.more'), icon: Menu },
  ];

  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isAdmin = isSuperAdmin || user?.isCompanyAdmin || user?.role === 'ADMIN' || user?.role === 'OWNER';

  // Helper pour traduire le rôle
  const getRoleLabel = (role?: string): string => {
    if (!role) return t('nav.roles.default');
    const key = `nav.roles.${role}`;
    const translated = t(key);
    return translated === key ? role : translated;
  };

  // Données utilisateur pour le ProfileWidget
  const displayName = user?.username || user?.fullName || user?.email?.split('@')[0] || t('nav.roles.default');
  const displayRole = getRoleLabel(user?.role);
  const userInitials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  // Merge background style with default
  const containerStyle = hasCustomBackground
    ? { ...backgroundStyle, minHeight: '100vh' }
    : { backgroundColor: '#dfdede', minHeight: '100vh' };

  return (
    <div className="h-screen overflow-hidden" style={containerStyle}>
      {/* Floating Profile Widget - Top Right - Desktop only */}
      <ProfileWidget
        username={displayName}
        role={displayRole}
        initials={userInitials}
        onLogout={handleLogout}
      />

      {/* Header - only on mobile */}
      <div className="md:hidden">
        <DashboardHeader user={user} onLogout={handleLogout} onMenuClick={() => setMenuOpen(true)} />
      </div>

      {/* Mobile Menu Drawer */}
      <MobileMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onLogout={handleLogout}
      />

      {/* Desktop Layout with Sidebar */}
      <div className="flex h-screen print:block">
        {/* Sidebar - desktop only - TRANSPARENT VOLVO DESIGN */}
        <div className={`print:hidden hidden md:flex md:flex-col bg-white/20 backdrop-blur-xl border-r border-gray-200/30 transition-all duration-300 relative ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
          {/* Company Switcher */}
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-3'} py-4 border-b border-gray-200/30 overflow-visible flex-shrink-0`}>
            <CompanySwitcher isCollapsed={sidebarCollapsed} />
          </div>

          {/* Collapse/Expand Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="
              absolute top-[5.5rem] -right-3 z-20
              bg-white/60 backdrop-blur-xl rounded-full p-1.5
              shadow-md border border-white/60
              hover:bg-white hover:shadow-lg hover:border-teal-300
              transition-all duration-300
              group
            "
            title={sidebarCollapsed ? t('nav.tooltips.expand') : t('nav.tooltips.collapse')}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-3.5 h-3.5 text-gray-500 group-hover:text-teal-600 transition-colors" />
            ) : (
              <ChevronLeft className="w-3.5 h-3.5 text-gray-500 group-hover:text-teal-600 transition-colors" />
            )}
          </button>

          {buildOptimizedSidebar({
            activeTab,
            onTabChange: handleTabChange,
            isAdmin,
            isSuperAdmin,
            expandedMenu,
            setExpandedMenu,
            badges,
            sidebarCollapsed,
            navigate,
            shouldShowSection,
            t,
            pathname: location.pathname,
          })}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden print:overflow-visible print:block">
          {/* Trial Banner */}
          <div className="print:hidden">
            <TrialBanner compact />
          </div>

          {/* Scrollable content */}
          <main className="flex-1 overflow-y-auto" style={hasCustomBackground ? { backgroundColor: 'transparent' } : { backgroundColor: '#dfdede' }}>
            <div className="h-full px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 md:pt-20 pb-20 md:pb-8">
              <div className="fade-in h-full">
                <Outlet context={{ user }} />
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Bottom Navigation Mobile */}
      <BottomNavigation items={navItems} activeId={activeTab} onChange={(id) => handleTabChange(id as Tab)} />
    </div>
  );
};
